import React, { useMemo, useState } from "react";
import {
  ExpandMore,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";

import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { ButtonTypes } from "../../../../../components/Button";
import { PaymentRowMenu } from "./PaymentRowMenu";
import { PAGE_SIZE, STATUS_LABEL, orderNewestFirst, sumPaid } from "./helpers";
import { PaymentHistoryProps } from "./types";
import {
  BalanceValue,
  Card,
  CardAmountRow,
  CardBalance,
  CardDate,
  CardDateWrap,
  CardList,
  CardNote,
  CardSplit,
  CardTop,
  CardTotal,
  CollapseChevron,
  CountPill,
  DateValue,
  DesktopHeader,
  Footer,
  FooterNote,
  HeaderLeft,
  HeaderMeta,
  HeaderTitle,
  InterestValue,
  LoadMoreButton,
  MenuCell,
  MobileHeader,
  MobileHeaderLeft,
  PaymentCard,
  PrincipalValue,
  StatusPill,
  Table,
  TableHead,
  TableHeadCell,
  TableRow,
  TotalValue,
} from "./styles";

export const PaymentHistory: React.FC<PaymentHistoryProps> = React.memo(
  ({ payments, onEditPayment, onDeletePayment }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const ordered = useMemo(() => orderNewestFirst(payments), [payments]);
    const totalPaid = useMemo(() => sumPaid(payments), [payments]);

    // Nothing to show while loading or once a loaded liability has no payments yet.
    if (payments.length === 0) {
      return null;
    }

    const visible = ordered.slice(0, visibleCount);
    const count = ordered.length;
    const hasMore = visibleCount < count;
    const nextChunk = Math.min(PAGE_SIZE, count - visibleCount);
    const countLabel = `${count} payment${count === 1 ? "" : "s"}`;
    const footerNote = `Showing ${visible.length} of ${count} · ${formatCurrency(
      totalPaid,
    )} paid in total`;

    const onLoadMore = () => setVisibleCount((current) => current + PAGE_SIZE);

    return (
      <Card data-testid="payment-history">
        <DesktopHeader>
          <HeaderLeft>
            <HeaderTitle>Payment history</HeaderTitle>
          </HeaderLeft>
          <HeaderMeta>{countLabel} · newest first</HeaderMeta>
        </DesktopHeader>

        <MobileHeader>
          <MobileHeaderLeft
            type="button"
            onClick={() => setIsExpanded((previous) => !previous)}
            aria-expanded={isExpanded}
          >
            <CollapseChevron $expanded={isExpanded} aria-hidden>
              <ExpandMore fontSize="small" />
            </CollapseChevron>
            <HeaderLeft>
              <HeaderTitle>Payment history</HeaderTitle>
            </HeaderLeft>
          </MobileHeaderLeft>
          <CountPill>{countLabel}</CountPill>
        </MobileHeader>

        <Table>
          <TableHead>
            <TableHeadCell>Date</TableHeadCell>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell $align="right">Total</TableHeadCell>
            <TableHeadCell $align="right">Principal</TableHeadCell>
            <TableHeadCell $align="right">Interest</TableHeadCell>
            <TableHeadCell $align="right">Balance</TableHeadCell>
            <TableHeadCell aria-hidden />
          </TableHead>

          {visible.map((payment) => (
            <TableRow key={payment.id}>
              <DateValue>
                {formatDate(payment.date, DateFormat.DMY_SPACE)}
              </DateValue>
              <StatusPill $status={payment.status}>
                {STATUS_LABEL[payment.status]}
              </StatusPill>
              <TotalValue>{formatCurrency(payment.amount)}</TotalValue>
              <PrincipalValue>
                {formatCurrency(payment.principalAmount)}
              </PrincipalValue>
              <InterestValue>
                {formatCurrency(payment.interestAmount)}
              </InterestValue>
              <BalanceValue>
                {formatCurrency(payment.balanceAfter)}
              </BalanceValue>
              <MenuCell>
                <PaymentRowMenu
                  payment={payment}
                  onEditPayment={onEditPayment}
                  onDeletePayment={onDeletePayment}
                />
              </MenuCell>
            </TableRow>
          ))}
        </Table>

        {isExpanded && (
          <CardList>
            {visible.map((payment) => (
              <PaymentCard key={payment.id}>
                <CardTop>
                  <CardDateWrap>
                    <CardDate>
                      {formatDate(payment.date, DateFormat.DMY_SPACE)}
                    </CardDate>
                    <StatusPill $status={payment.status}>
                      {STATUS_LABEL[payment.status]}
                    </StatusPill>
                  </CardDateWrap>
                  <PaymentRowMenu
                    payment={payment}
                    onEditPayment={onEditPayment}
                    onDeletePayment={onDeletePayment}
                  />
                </CardTop>

                <CardAmountRow>
                  <CardTotal>{formatCurrency(payment.amount)}</CardTotal>
                  <CardBalance>
                    Balance {formatCurrency(payment.balanceAfter)}
                  </CardBalance>
                </CardAmountRow>

                <CardSplit>
                  Principal{" "}
                  <strong>{formatCurrency(payment.principalAmount)}</strong>
                  {" · "}
                  Interest {formatCurrency(payment.interestAmount)}
                </CardSplit>

                {payment.note && <CardNote>{payment.note}</CardNote>}
              </PaymentCard>
            ))}
          </CardList>
        )}

        <Footer>
          <FooterNote>{footerNote}</FooterNote>
          {hasMore && (
            <LoadMoreButton
              type={ButtonTypes.primary}
              size="small"
              onClick={onLoadMore}
            >
              Load {nextChunk} more
              <ArrowDownIcon aria-hidden />
            </LoadMoreButton>
          )}
        </Footer>
      </Card>
    );
  },
);

PaymentHistory.displayName = "PaymentHistory";
